/**
 * Katalog veřejných GTFS feedů (ověřené oficiální zdroje) + stahování
 * s průběhem a srozumitelným ošetřením CORS/síťových chyb.
 *
 * Oficiální celostátní GTFS pro ČR neexistuje (Správa železnic publikuje
 * CZPTT XML) — další dopravce lze připojit polem „URL GTFS zipu".
 */

export interface FeedDef {
  id: string;
  name: string;
  /** Same-origin proxy cesta (dev/preview server, viz vite.config.ts). */
  proxyPath: string;
  /** Přímá URL — pro ruční stažení a prostředí s vlastní proxy. */
  directUrl: string;
  note: string;
}

export const FEED_CATALOG: FeedDef[] = [
  {
    id: 'pid',
    name: 'Praha a Střední Čechy (PID)',
    proxyPath: '/feeds/pid.zip',
    directUrl: 'https://data.pid.cz/PID_GTFS.zip',
    note: 'Všechny spoje PID: metro, tramvaje, autobusy, vlaky, přívozy, lanovka. Denní aktualizace, ~50 MB.',
  },
  {
    id: 'idsjmk',
    name: 'Brno a Jihomoravský kraj (IDS JMK)',
    proxyPath: '/feeds/idsjmk.zip',
    directUrl: 'https://kordis-jmk.cz/gtfs/gtfs.zip',
    note: 'Všechny spoje IDS JMK. Týdenní aktualizace.',
  },
];

export interface FetchProgress {
  loadedBytes: number;
  totalBytes: number | null;
}

/** Validátory pro podmíněné stažení („aktualizuj jen při změně souboru"). */
export interface FeedValidators {
  etag: string | null;
  lastModified: string | null;
}

export type FetchFeedResult =
  | { notModified: true }
  | { notModified: false; bytes: Uint8Array; validators: FeedValidators };

/**
 * Stáhne GTFS zip s hlášením průběhu. S `validators` posílá podmíněný
 * požadavek (If-None-Match / If-Modified-Since) — když se soubor na
 * serveru nezměnil, vrátí `notModified` a nic se nestahuje.
 * Ověřuje, že odpověď je opravdu zip (magické bajty „PK") — proxy bez
 * konfigurace by jinak vrátila HTML stránku aplikace.
 */
export async function fetchGtfsZip(
  url: string,
  onProgress?: (p: FetchProgress) => void,
  validators?: FeedValidators | null,
): Promise<FetchFeedResult> {
  const headers: Record<string, string> = { Accept: 'application/zip,application/octet-stream,*/*' };
  if (validators?.etag) headers['If-None-Match'] = validators.etag;
  if (validators?.lastModified) headers['If-Modified-Since'] = validators.lastModified;
  let res: Response;
  try {
    res = await fetch(url, { headers });
  } catch {
    throw new Error(
      'Stažení selhalo — server nepovoluje přístup z prohlížeče (CORS), nebo jste offline. ' +
        'Stáhněte zip ručně v novém panelu a použijte „Nahrát zip".',
    );
  }
  if (res.status === 304) return { notModified: true };
  if (!res.ok) {
    throw new Error(`Stažení selhalo (HTTP ${res.status}). Zkuste to později, nebo soubor stáhněte ručně.`);
  }
  const total = Number(res.headers.get('Content-Length')) || null;
  let out: Uint8Array;
  if (!res.body) {
    out = new Uint8Array(await res.arrayBuffer());
    onProgress?.({ loadedBytes: out.length, totalBytes: total });
  } else {
    const reader = res.body.getReader();
    const chunks: Uint8Array[] = [];
    let loaded = 0;
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
      loaded += value.length;
      onProgress?.({ loadedBytes: loaded, totalBytes: total });
    }
    out = new Uint8Array(loaded);
    let off = 0;
    for (const c of chunks) {
      out.set(c, off);
      off += c.length;
    }
  }
  if (out.length < 4 || out[0] !== 0x50 || out[1] !== 0x4b) {
    throw new Error('Server nevrátil zip soubor (proxy není nakonfigurovaná?). Stáhněte zip ručně a použijte „Nahrát zip".');
  }
  return {
    notModified: false,
    bytes: out,
    validators: { etag: res.headers.get('ETag'), lastModified: res.headers.get('Last-Modified') },
  };
}

/**
 * Stáhne feed z katalogu: nejdřív same-origin proxy (dev/preview server),
 * při neúspěchu přímou URL (funguje jen s vlastní proxy/CORS).
 */
export async function fetchCatalogFeed(
  feed: FeedDef,
  onProgress?: (p: FetchProgress) => void,
  validators?: FeedValidators | null,
): Promise<FetchFeedResult> {
  try {
    return await fetchGtfsZip(feed.proxyPath, onProgress, validators);
  } catch {
    return await fetchGtfsZip(feed.directUrl, onProgress, validators);
  }
}

export function formatBytes(n: number): string {
  if (n >= 1 << 20) return `${(n / (1 << 20)).toFixed(1)} MB`;
  if (n >= 1 << 10) return `${Math.round(n / (1 << 10))} kB`;
  return `${n} B`;
}
